/**
 * The global announcement banner (`config/global.announcementBanner`, spec 13
 * › AnnouncementBanner): what `setAnnouncementBanner` accepts and how the
 * admin's form follows production (ux-admin-03, ux-admin-08).
 *
 * The web shows `textEs` to a Spanish UI and `textEn` to every other language,
 * and hides the banner when that text is empty
 * (`apps/web/src/lib/hooks/use-visible-announcement.ts`). So a banner that is
 * on needs both texts.
 */
export const EMPTY_ANNOUNCEMENT_BANNER = { enabled: false, textEn: "", textEs: "" };
/** Texts trimmed: what gets stored. */
export function normalizeAnnouncementBanner(banner) {
    return { enabled: banner.enabled, textEn: banner.textEn.trim(), textEs: banner.textEs.trim() };
}
/** What stops `banner` from being saved: with `enabled`, both texts (trimmed) are required. */
export function announcementBannerProblems(banner) {
    if (!banner.enabled)
        return [];
    const problems = [];
    if (!banner.textEn.trim())
        problems.push("text_en_required");
    if (!banner.textEs.trim())
        problems.push("text_es_required");
    return problems;
}
export function sameAnnouncementBanner(a, b) {
    return a.enabled === b.enabled && a.textEn === b.textEn && a.textEs === b.textEs;
}
function liveOrEmpty(live) {
    return live ?? EMPTY_ANNOUNCEMENT_BANNER;
}
/** What the draft is compared with: the accepted save a snapshot has not confirmed yet, else `live`. */
function savedBaseline(state) {
    return state.pendingSaved ?? liveOrEmpty(state.live);
}
export function initAnnouncementBannerForm(live) {
    return { live, pendingSaved: null, draft: liveOrEmpty(live), dirty: false };
}
export function announcementBannerFormReducer(state, action) {
    switch (action.type) {
        case "live": {
            // A snapshot that carries the accepted save confirms it; an older one (another
            // config/global field changed before the save landed) leaves it pending.
            const pendingSaved = state.pendingSaved && !sameAnnouncementBanner(state.pendingSaved, liveOrEmpty(action.banner))
                ? state.pendingSaved
                : null;
            const baseline = savedBaseline({ live: action.banner, pendingSaved });
            if (!state.dirty)
                return { live: action.banner, pendingSaved, draft: baseline, dirty: false };
            // Unsaved edits stay; they stop being edits once production matches them.
            return { ...state, live: action.banner, pendingSaved, dirty: !sameAnnouncementBanner(state.draft, baseline) };
        }
        case "edit": {
            const draft = { ...state.draft, ...action.patch };
            return { ...state, draft, dirty: !sameAnnouncementBanner(draft, savedBaseline(state)) };
        }
        case "saved": {
            const pendingSaved = sameAnnouncementBanner(action.banner, liveOrEmpty(state.live)) ? null : action.banner;
            // The draft that was saved takes its trimmed form; one edited while saving stays as typed.
            if (sameAnnouncementBanner(normalizeAnnouncementBanner(state.draft), action.banner)) {
                return { ...state, pendingSaved, draft: action.banner, dirty: false };
            }
            return { ...state, pendingSaved, dirty: !sameAnnouncementBanner(state.draft, action.banner) };
        }
    }
}
/** Whether the form can save: production state known, edits pending, nothing missing. */
export function canSaveAnnouncementBanner(form) {
    return form.live !== undefined && form.dirty && announcementBannerProblems(form.draft).length === 0;
}
//# sourceMappingURL=announcement-banner.js.map